<template>
  <div :id="'div' + divName" class="row f-row table_chart_height mt-1">
    <div class="row f-row" style="height:100%; width:100%;">
      <div class="col col-12 h-100">
        <div
          class="col col-12 text-center"
          v-if="!loadingAll && tableFormat.length === 0"
          style="width: 100%; top: calc(50% - 8px)!important; position: absolute;"
        >
          {{ noDataStr }}
        </div>
        <div
          v-else
          class="container__table-borderless"
          :style="{ 'padding-right': (isLarge ? 48 : 40) + 'px!important' }"
          style="padding-left: 1px"
        >
          <table
            class="table table-sm table-borderless curve_analysis_table"
            :class="isTotals && 'table__totals'"
          >
            <thead class="table-headers text-center align-center thead">
              <tr v-if="!isLarge">
                <th
                  :style="{
                    width: `${Math.round(
                      !isLarge ? divWidth / 12 : (4 * divWidth) / 12,
                    )}px !important`,
                  }"
                >
                  Date range
                </th>
                <td>{{ dateValue }}</td>
              </tr>
            </thead>
            <tbody class="text-center align-center thead">
              <template v-for="(dat, idx) in tableFormat">
                <tr
                  :key="idx"
                  :class="{
                    'div-prices': dat.divP,
                    'div-quantity': dat.divQ,
                    'table-totals': dat.totalRow,
                  }"
                  :style="{
                    height: dat.labelTable === 'Strat. Price' ? '40px' : 'auto',
                  }"
                  v-if="!dat.isDetails || showDetails"
                >
                  <th
                    :style="widthTh()"
                    :class="{
                      ['text__prices' + divName]:
                        tableFormat.some((d) => d.divP) && idx === 0,
                      ['text__quantities' + divName]:
                        idx === tableFormat.length - 1,
                    }"
                  >
                    <span
                      v-if="!dat.isPrices && dat.labelTable !== 'Required'"
                      class="expand-arrow"
                      @click="toggleRow(idx)"
                      style="cursor: pointer; margin-right: 5px;"
                    >
                      <i
                        :class="
                          `fa fa-chevron-${
                            expandedRows[idx] ? 'down' : 'right'
                          }`
                        "
                        aria-hidden="true"
                      ></i>
                    </span>
                    {{ dat.labelTable }}
                    <template v-if="!dat.totalRow">
                      <template v-if="dat.isPrices">
                        P ({{ `${dat.sUnit}/${dat.unit}` }})
                      </template>
                      <template v-else>
                        {{
                          dat[isCurrency ? "sUnit" : "unit"]
                            ? `Q (${dat[isCurrency ? "sUnit" : "unit"]})`
                            : "Q"
                        }}
                      </template>
                    </template>
                  </th>
                  <td
                    v-for="(item, index) in zoomedData"
                    v-bind:key="index"
                    :class="{ tableHovered: isRowHovered(dat, item) }"
                    :style="{
                      'background-color': dat['colorBckTable'],
                      'border-bottom': '1px solid ' + dat['colorBdr'],
                    }"
                    @mouseover="onTableHover(dat, item, true, index)"
                    @mouseleave="onTableHover(dat, item, false, index)"
                  >
                    <span>{{ formatItem(item, dat) }}</span>
                    <span v-if="zoomedData.length < 16">
                      <template
                        v-if="!dat.isPrices && dat.labelTable !== 'Required'"
                      >
                        ({{
                          !item
                            ? "-"
                            : regex.test(item[dat.unitPerc])
                            ? item[dat.unitPerc]
                            : dat.formatPerc(item[dat.unitPerc])
                        }})
                      </template>
                    </span>
                  </td>
                </tr>
                <!-- Sub-rows -->
                <tr
                  v-if="
                    dat.labelTable !== 'Required' &&
                      !dat.isPrices &&
                      expandedRows[idx]
                  "
                  :class="{ 'div-prices': dat.divP, 'div-quantity': dat.divQ }"
                  :key="`sub-${idx}`"
                >
                  <th :style="widthTh()">
                    <template></template>
                  </th>
                  <td
                    v-for="(item, index) in zoomedData"
                    v-bind:key="index"
                    :class="{ tableHovered: isRowHovered(dat, item) }"
                    :style="{
                      'background-color': dat['colorBckTable'],
                      'border-bottom': '1px solid ' + dat['colorBdr'],
                    }"
                    @mouseover="onTableHover(dat, item, true, index)"
                    @mouseleave="onTableHover(dat, item, false, index)"
                  >
                    <span
                      >Δ
                      {{
                        formatItem(item, {
                          ...dat,
                          nameData: validateIsTotalRow(dat)
                            ? "delta"
                            : `delta_${removeQuantitySuffix(dat.nameData)}`,
                        })
                      }}</span
                    >
                    <span v-if="zoomedData.length < 16">
                      ({{
                        !item
                          ? "-"
                          : formatItem(item, {
                              ...dat,
                              nameData: validateIsTotalRow(dat)
                                ? "q_min"
                                : `min_${removeQuantitySuffix(dat.nameData)}`,
                            })
                      }}
                      |
                      {{
                        !item
                          ? "-"
                          : formatItem(item, {
                              ...dat,
                              nameData: validateIsTotalRow(dat)
                                ? "q_max"
                                : `max_${removeQuantitySuffix(dat.nameData)}`,
                            })
                      }})
                    </span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { select } from "d3";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";

export default {
  name: "CommodityStrategyTable",
  props: {
    Analytics: { required: true, default: defaultAnalytics() },
    colorTheme: { required: true, type: Boolean, default: true },
    divName: { required: false, default: "table" },
    formatVar: {
      default: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      },
    },
    isLarge: { required: false, type: Boolean, default: true },
    isTotals: { required: false, type: Boolean, default: false },
    loadingAll: { required: true, type: Boolean, default: false },
    noDataStr: { required: false, type: String },
    renderChartSignal: { required: true, default: 0 },
    showDetails: { required: true, type: Boolean, default: false },
    tableFormat: { required: true, type: Array, default: () => [] },
    zoomedData: { required: true, default: () => [] },
  },
  data() {
    return {
      divWidth: 100,
      regex: /\||%/,
      formatVars: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
        divContainerTableTotalsWidth: "",
      },
      expandedRows: {},
    };
  },
  computed: {
    dateValue() {
      return this.zoomedData[0] != null
        ? Object.keys(this.zoomedData[0]).indexOf("date") !== -1
          ? this.zoomedData[0]["date"]
          : "date"
        : "date";
    },
    isCurrency() {
      return this.Analytics.assetType === "currency";
    },
  },
  mounted() {
    this.calculateDivWidth();
  },
  watch: {
    renderChartSignal() {
      this.calculateDivWidth();
    },
    formatVar: {
      handler(newVal) {
        this.getChangeTableHover(newVal);
      },
      deep: true,
    },
  },
  methods: {
    calculateDivWidth() {
      let element = document.getElementById("div" + this.divName);
      if (element != null) {
        this.divWidth = element.clientWidth;
        const { textP, textQ, topP, topQ } = this.getTextValues(
          this.tableFormat.length,
        );
        this.generateTextStyle(textP, `text__prices${this.divName}`, topP);
        this.generateTextStyle(textQ, `text__quantities${this.divName}`, topQ);
      }
    },
    formatItem(item, dat) {
      if (
        item === null ||
        item[dat.nameData] === null ||
        Number.isNaN(item[dat.nameData])
      ) {
        return "-";
      }
      return dat.formatF(item[dat.nameData]);
    },
    widthTh() {
      const containerLegends = select(".container-legends_chart_quantity");
      const widthContainerLegends = containerLegends.node()
        ? containerLegends.node().clientWidth
        : this.isLarge
        ? this.divWidth / 12 - 10
        : (4 * this.divWidth) / 12;

      return {
        width: `${widthContainerLegends - 5}px !important`,
      };
    },
    getTextValues(tableFormatLength) {
      const textInfo = {
        short: {
          textP: "Px",
          textQ: "Qty",
          topP: 0,
          topQ: 25,
        },
        long: {
          textP: "Prices",
          textQ: "Quantities",
          topP: 50,
          topQ: -70,
        },
      };
      return tableFormatLength < 3 ? textInfo.short : textInfo.long;
    },
    generateTextStyle(content, selector, top) {
      const styleCSS = document.createElement("style");
      const divContainerTable = select(".curve_analysis_table").node();
      const divContainerTableWidth = divContainerTable
        ? divContainerTable.clientWidth
        : "";

      if (this.isTotals) {
        const divContainerTableTotals = select(
          ".curve_analysis_table.table__totals",
        ).node();
        this.formatVars.divContainerTableTotalsWidth = divContainerTableTotals
          ? divContainerTableTotals.clientWidth
          : "";
      }

      styleCSS.innerHTML = `
        .${selector} {
          position: relative;
        }
        th.${selector}::after {
          content: "${content}";
          position: absolute;
          transform: rotate(-90deg);
          top: ${top}%;
          transform-origin: center;
          white-space: nowrap;
          font-size: 12px;
          color: var(--white-gray);
          z-index: 1;
          text-shadow: 1px 1px 1px rgb(180 180 180 / 20%);
        }
        .curve_analysis_table tbody tr th.${selector}::after {
          left: ${divContainerTableWidth}px;
        }
        .curve_analysis_table.table__totals tbody tr th.${selector}::after {
          left: ${this.formatVars.divContainerTableTotalsWidth}px !important;
        }
      `;
      document.head.appendChild(styleCSS);
    },
    onTableHover(dat, item, isHovered) {
      select(`.bar${dat["elementName"]}${+item.date}`).classed(
        "hoved",
        isHovered,
      );
      select(
        `.${dat["figureElements"]}${dat["elementName"]}${+item.date}`,
      ).classed("hoved", isHovered);
      select(`.${dat["elementName"]}${+item.date}`).classed("hoved", isHovered);

      Object.assign(this.formatVars, {
        userHovered: {
          date: +item.date,
          variable: dat["elementName"],
          hovered: isHovered,
          fromChart: false,
        },
      });
    },
    getChangeTableHover(val) {
      Object.assign(this.formatVars, {
        userHovered: {
          date: val.date,
          variable: val.variable,
          hovered: false,
          fromChart: val.fromChart,
        },
      });
    },
    toggleRow(index) {
      this.$set(this.expandedRows, index, !this.expandedRows[index]);
    },
    isRowHovered(dat, item) {
      return (
        (this.formatVars.userHovered.date === +item.date &&
          this.formatVars.userHovered.variable === dat.elementName &&
          this.formatVars.userHovered.hovered) ||
        (this.formatVars.userHovered.fromChart &&
          this.formatVars.userHovered.date === +item.date &&
          dat.elementName.startsWith(this.formatVars.userHovered.variable))
      );
    },
    removeQuantitySuffix(element) {
      return element.replace("q_", "");
    },
    validateIsTotalRow(dat) {
      return dat.totalRow;
    },
  },
};
</script>

<style scoped>
.expand-arrow {
  font-size: 14px;
  color: var(--textactive);
}

.sub-row {
  transition: all 0.3s ease;
}

table tr.table-totals th {
  background-color: var(--graylightbackground-alpha4);
}

tbody tr:hover {
  background-color: initial !important;
}
</style>
